import React from "react";
import moment from "moment";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Sidebar } from "primereact/sidebar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_PERSON, { UPDATE_PERSON } from "../../provider/People/mutation";
import { PEOPLE, PERSON } from "../../provider/People/query";
import { MEDICINES } from "../../provider/Medicines/query";

/** components */
import Sorting from "../../components/Sorting";
import Call from "../../components/Call";
import History from "../../components/History";

/** utils */
import { getGraphsqErros, maskCpfCnpj } from "../../utilities/functions";

/** services */
import { getUser } from "../../services/auth";

const Crud = () => {
    const settings = useSettings();
    const apollo = useApolloClient();

    const toast = React.useRef<any>(null);
    const dt = React.useRef<any>(null);

    const [records, setRecords] = React.useState<any[]>([]);
    const [record, setRecord] = React.useState<iPeople>();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
    const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [globalFilter, setGlobalFilter] = React.useState<any>(null);

    const [visibleSorting, setVisibleSorting] = React.useState(false);
    const [visibleHistory, setVisibleHistory] = React.useState(false);
    const [visibleCall, setVisibleCall] = React.useState(false);

    const [medicines, setMedicines] = React.useState<iMedicine[]>([]);

    let emptyRecord: iPeople = {
        name: "",
        cpf: "",
        city: "",
        age: "",
        birth_date: "",
        region: "",
        rg: "",
    };

    const loadRecords = (cleanRecord: boolean = false) => {
        setLoading(true);

        apollo
            .query({
                query: PEOPLE,
                variables: {
                    // limit: perPage,
                    // filter: filter,
                },
                fetchPolicy: "no-cache",
            })
            .then(response => {
                const data = response.data.people;

                setRecords(data);
                setLoading(false);
            })
            .catch(error => {
                setRecords([]);
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const loadRecord = async (record_id: any, callback: Function) => {
        setLoading(true);

        apollo
            .query({
                query: PERSON,
                variables: {
                    id: parseInt(`${record_id}`),
                },
                fetchPolicy: "no-cache",
            })
            .then(response => {
                const data: iPeople = response.data.person;

                setMedicines(response.data.medicines);

                setRecord(data);
                setLoading(false);

                callback();
            })
            .catch(error => {
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const openNew = () => {
        setRecord(emptyRecord);
        setSubmitted(false);
        setRecordDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRecordDialog(false);
    };

    const hideDeleteRecordDialog = () => {
        setDeleteRecordDialog(false);
    };

    const saveRecord = () => {
        setSubmitted(true);

        if (!record?.name || !record?.city) {
            return;
        }

        const _record: any = { ...record };

        delete _record.__typename;
        delete _record.call;
        delete _record.calls;

        if (_record.age) _record.age = parseInt(`${_record.age}`);

        setLoading(true);
        let mutation = CREATE_PERSON;

        if (record?.id) {
            _record.id = parseInt(`${_record.id}`);
            mutation = UPDATE_PERSON;

            delete _record.key;
        }

        apollo
            .mutate({
                mutation,
                variables: {
                    data: {
                        ..._record,
                    },
                },
            })
            .then(response => {
                setLoading(false);

                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

                loadRecords();
                setRecordDialog(false);
                setRecord(emptyRecord);
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const editRecord = (record: iPeople) => {
        const _record: iPeople = { ...record };

        if (_record.birth_date) _record.birth_date = moment(_record.birth_date).utc().format("YYYY-MM-DD");
        setRecord({ ..._record });
        setRecordDialog(true);
    };

    const newSorting = (record: iPeople) => {
        loadRecord(record.id, () => setVisibleSorting(true));
    };

    const newCall = (record: iPeople) => {
        loadRecord(record.id, () => setVisibleCall(true));
    };

    const showHistory = (record: iPeople) => {
        loadRecord(record.id, () => setVisibleHistory(true));
    };

    const deleteRecord = () => {
        apollo
            .mutate({
                mutation: UPDATE_PERSON,
                variables: {
                    data: {
                        id: parseInt(`${record?.id}`),
                        is_deleted: true,
                    },
                },
            })
            .then(response => {
                let _records = records.filter((val: any) => val.id !== record?.id);
                setRecords(_records);
                setDeleteRecordDialog(false);
                setRecord(emptyRecord);
                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const calcAge = (rowData: iPeople | undefined) => {
        if (!rowData || !rowData.birth_date) return "";

        const age = moment().diff(rowData.birth_date, "years");
        return age === 0 ? "<1" : age;
    };

    const onInputChange = (e: any, name: string) => {
        let val = (e.target && e.target.value) || "";

        let _record: any = { ...record };

        if (name === "cpf") _record[name] = maskCpfCnpj(val, "CPF");
        else _record[name] = val;

        if (name === "birth_date") {
            const age = moment().utc().diff(moment(val).utc(), "years");
            _record["age"] = `${age}`;
        }

        setRecord(_record);
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
                {/* <Button
                    label="Delete"
                    icon="pi pi-trash"
                    className="p-button-danger mb-2"
                    onClick={confirmDeleteSelected}
                    disabled={!selectedRecords || !selectedRecords.length}
                /> */}
            </React.Fragment>
        );
    };

    const codeBodyTemplate = (rowData: iPeople) => {
        return (
            <>
                <span className="p-column-title">Cód.</span>
                {rowData.cpf}
            </>
        );
    };

    const nameBodyTemplate = (rowData: iPeople) => {
        return (
            <>
                <span className="p-column-title">Nome</span>
                {rowData.name}
            </>
        );
    };

    const cityBodyTemplate = (rowData: iPeople) => {
        return (
            <>
                <span className="p-column-title">Cidade</span>
                {rowData.city}
            </>
        );
    };

    const regionBodyTemplate = (rowData: iPeople) => {
        return (
            <>
                <span className="p-column-title">Região</span>
                {rowData.region}
            </>
        );
    };

    const birtDateBodyTemplate = (rowData: iPeople) => {
        return (
            <>
                <span className="p-column-title">Data de nascimento</span>
                {rowData.birth_date ? `${moment(rowData.birth_date).utc().format("DD/MM/YYYY")} (${calcAge(rowData)})` : "-"}
            </>
        );
    };

    const confirmDeleteRecord = (record: any) => {
        setRecord(record);
        setDeleteRecordDialog(true);
    };

    const actionBodyTemplate = (rowData: iPeople) => {
        return (
            <div className="actions">
                <Button label="" tooltip="Editar" icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />

                {getUser()?.role === "ADMIN" ? (
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0 mr-2" onClick={() => confirmDeleteRecord(rowData)} />
                ) : null}

                <Button label="" tooltip="Histórico" icon="pi pi-list" className="p-button-rounded p-button-info mr-2" onClick={() => showHistory(rowData)} />

                {getUser()?.role === "NURSE" || getUser()?.role === "ADMIN" ? (
                    <Button
                        label="Triagem"
                        icon="pi pi-arrow-right-arrow-left"
                        className="p-button-rounded p-button-success mr-2"
                        onClick={() => newSorting(rowData)}
                    />
                ) : null}

                {getUser()?.role !== "NURSE" ? (
                    <Button
                        label={rowData.call?.id ? "Atendimento realizado" : "RealizarAtendimento"}
                        icon="pi pi-thumbs-up-fill"
                        className="p-button-rounded p-button-dark"
                        onClick={() => newCall(rowData)}
                    />
                ) : null}
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{loading ? "Carregando pessoas..." : "Pessoas"}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText autoFocus className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
            </span>
        </div>
    );

    const recordDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
            <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
        </>
    );

    const deleteRecordDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
            <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
        </>
    );

    React.useEffect(() => {
        loadRecords(true);
    }, []);

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={records}
                        dataKey="id"
                        paginator
                        stripedRows={true}
                        rowHover
                        showGridlines
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        globalFilter={globalFilter}
                        filterDelay={500}
                        emptyMessage="Nenhum registro encontrado"
                        header={header}
                        responsiveLayout="scroll">
                        <Column field="cpf" header="ID" sortable body={codeBodyTemplate} headerStyle={{}}></Column>
                        <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
                        <Column field="city" header="Cidade" body={cityBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="region" header="Região" body={regionBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="age" header="Data de nascimento" body={birtDateBodyTemplate} sortable headerStyle={{}}></Column>

                        <Column header="Ações" className="flex flex-row align-items-start justify-content-end" body={actionBodyTemplate}></Column>
                    </DataTable>

                    {/* Modal adicionar editar */}
                    <Dialog
                        visible={recordDialog}
                        style={{ width: "550px" }}
                        header={record?.id ? "Detalhes da Pessoa" : "Nova Pessoa"}
                        modal
                        className="p-fluid"
                        footer={recordDialogFooter}
                        onHide={hideDialog}>
                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="name">* Nome</label>
                                <InputText
                                    id="name"
                                    value={record?.name}
                                    onChange={e => onInputChange(e, "name")}
                                    required
                                    autoFocus
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                                />
                                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
                            </div>
                        </div>

                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="city">* Cidade</label>
                                <InputText
                                    id="city"
                                    value={record?.city}
                                    onChange={e => onInputChange(e, "city")}
                                    required
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.city })}
                                />
                                {submitted && !record?.city && <small className="p-invalid">Cidade é obrigatório.</small>}
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="region">Região</label>
                                <InputText id="region" value={record?.region} onChange={e => onInputChange(e, "region")} className={"p-3"} />
                            </div>
                        </div>

                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="cpf">CPF</label>
                                <InputText id="cpf" value={record?.cpf} onChange={e => onInputChange(e, "cpf")} className={"p-3"} />
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="rg">RG</label>
                                <InputText id="rg" value={record?.rg} onChange={e => onInputChange(e, "rg")} className={"p-3"} />
                            </div>
                        </div>

                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="birth_date">Data de nascimento</label>
                                <InputText
                                    id="birth_date"
                                    type={"date"}
                                    value={record?.birth_date}
                                    onChange={e => onInputChange(e, "birth_date")}
                                    className={"p-3"}
                                />
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="age">Idade</label>
                                <InputText
                                    id="age"
                                    type={"number"}
                                    disabled
                                    value={calcAge(record)}
                                    onChange={e => onInputChange(e, "age")}
                                    className={"p-3"}
                                />
                            </div>
                        </div>
                    </Dialog>

                    {/* Modal remover registro unico */}
                    <Dialog
                        visible={deleteRecordDialog}
                        // style={{ width: "450px" }}
                        header="Atenção"
                        modal
                        footer={deleteRecordDialogFooter}
                        onHide={hideDeleteRecordDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {record && (
                                <span>
                                    Tem certeza de que deseja excluir <b>{record?.name}</b>?
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
            <Sidebar style={{ minWidth: "500px" }} visible={visibleSorting} onHide={() => setVisibleSorting(false)} baseZIndex={1000000} position="right">
                <h3 className="mb-5" style={{ fontWeight: "normal" }}>
                    Triagem
                </h3>
                <Sorting person={record} setVisibleSorting={setVisibleSorting} />
            </Sidebar>

            <Sidebar style={{ minWidth: "60vw" }} visible={visibleCall} onHide={() => setVisibleCall(false)} baseZIndex={1000000} position="right">
                <h3 className="mb-0 pb-0" style={{ fontWeight: "bold" }}>
                    {record?.name}
                </h3>
                <h5 className="mb-0 ml-1 mt-2" style={{ fontWeight: "normal", fontSize: 12 }}>
                    {record?.birth_date ? `${moment(record?.birth_date).utc().format("DD/MM/YYYY")}, ${calcAge(record)} anos` : "-"}
                </h5>
                <h5 className="mb-5 ml-1 mt-2" style={{ fontWeight: "normal", fontSize: 12 }}>
                    Médico: <b>{getUser()?.name}</b>
                </h5>

                <Call
                    callback={loadRecords}
                    medicines={medicines.filter(i => !i.is_deleted)}
                    person={record}
                    setPerson={setRecord}
                    setVisibleCall={setVisibleCall}
                />
            </Sidebar>

            <Sidebar style={{ minWidth: "60vw" }} visible={visibleHistory} onHide={() => setVisibleHistory(false)} baseZIndex={1000000} position="right">
                <h3 className="mb-0 pb-0" style={{ fontWeight: "bold" }}>
                    {record?.name}
                </h3>
                <h5 className="mb-5 ml-1 mt-2" style={{ fontWeight: "normal", fontSize: 12 }}>
                    {record?.birth_date ? `${moment(record?.birth_date).utc().format("DD/MM/YYYY")}, ${record.age} anos` : "-"}
                </h5>

                <History medicines={medicines} person={record} setVisibleHistory={setVisibleHistory} />
            </Sidebar>
        </div>
    );
};

export default Crud;
